/**
 * Ingredient substitution lookup.
 *
 * This service used to normalize a line with its own private rules and then
 * scan the substitution table for any key that appeared as a SUBSTRING, in
 * object-literal declaration order, returning the first hit. `rice` is declared
 * at line 215 of the table and `wine` at line 367, so "Shaoxing rice wine or dry
 * sherry" offered quinoa, cauliflower rice and bulgur. It also asked whether the
 * KEY contained the line, which is how "salt" reached butter (via the alias
 * "unsalted butter") and "Sauce" reached soy sauce.
 *
 * Identity now comes from `resolveIngredient`, so this file only decides what to
 * suggest, never what the ingredient is.
 */

import {
  ingredientSubstitutions,
  IngredientSubstitution,
  Substitute,
} from '../data/ingredientSubstitutions';
import { resolveIngredient, acceptableIds } from '../utils/resolveIngredient';
import {
  broaderIds,
  displayName,
  type CanonicalIngredientId,
} from '../utils/ingredientVocabulary';

export interface SubstitutionResult {
  /** Display name of the ingredient the suggestions are for. */
  ingredient: string;
  found: boolean;
  substitutes: Substitute[];
  category?: string;
  /** Canonical id the line resolved to, when it resolved at all. */
  canonicalId?: CanonicalIngredientId | null;
}

/**
 * Table key -> canonical id, built once.
 *
 * The table predates the vocabulary and is keyed by English display strings, so
 * the keys are resolved through the same function everything else uses rather
 * than being hand-mapped. A key the vocabulary does not know simply keeps its
 * own name and stays reachable by exact text.
 */
const KEY_TO_ID = new Map<string, CanonicalIngredientId>();
const ID_TO_KEY = new Map<CanonicalIngredientId, string>();

for (const key of Object.keys(ingredientSubstitutions)) {
  const id = resolveIngredient(key).canonicalId;
  if (!id) continue;
  KEY_TO_ID.set(key, id);
  // First key wins, so `rice` claims `rice` before `white rice` can.
  if (!ID_TO_KEY.has(id)) ID_TO_KEY.set(id, key);
}

/**
 * Canonical id of each substitute, so a caller can ask "do I own one of these?".
 *
 * Of the 331 distinct substitute names in the table, 56 are compound recipes
 * ("grape juice + vinegar") rather than ingredients. Those resolve to null and
 * are excluded from pantry matching — you cannot own half a recipe — while
 * still rendering in the suggestion list.
 */
const SUBSTITUTE_IDS = new Map<string, CanonicalIngredientId | null>();

export function substituteId(name: string): CanonicalIngredientId | null {
  if (SUBSTITUTE_IDS.has(name)) return SUBSTITUTE_IDS.get(name)!;
  const compound = /\+|\bor\b/i.test(name);
  const id = compound ? null : resolveIngredient(name).canonicalId;
  SUBSTITUTE_IDS.set(name, id);
  return id;
}

function resultFor(key: string, id: CanonicalIngredientId | null, language: 'en' | 'fr'): SubstitutionResult {
  const data: IngredientSubstitution = ingredientSubstitutions[key];
  return {
    ingredient: id ? displayName(id, language) : key,
    found: true,
    substitutes: data.substitutes,
    category: data.category,
    canonicalId: id,
  };
}

/**
 * Suggestions for an ingredient line.
 *
 * Looks up the line's canonical identity, then that identity's base when the
 * line named a form of something ("lime juice" falls back to lime's
 * substitutes). Never falls back to substring overlap.
 */
export function findSubstitutions(ingredientName: string, language: string = 'en'): SubstitutionResult {
  const ref = resolveIngredient(ingredientName);
  const reader = language.toLowerCase().startsWith('fr') ? 'fr' : 'en';

  // The line's own identity, then anything it said was acceptable instead.
  for (const id of acceptableIds(ref)) {
    const key = ID_TO_KEY.get(id);
    if (key) return resultFor(key, id, reader);
  }

  // Something broader that we do know: lime juice borrows lime's suggestions,
  // and a chicken thigh borrows chicken's. Walking outward is what stops a
  // specific food from having no suggestions at all just because the table was
  // written in terms of the general one.
  for (const broader of broaderIds(ref.canonicalId)) {
    const key = ID_TO_KEY.get(broader);
    if (key) return resultFor(key, broader, reader);
  }

  // Last resort: the table is keyed by English text and may hold a name the
  // vocabulary has not learned yet. Exact key only — no substring scan.
  const literal = ingredientName.trim().toLowerCase();
  if (ingredientSubstitutions[literal]) {
    return resultFor(literal, KEY_TO_ID.get(literal) ?? null, reader);
  }

  return {
    ingredient: ref.canonicalId ? displayName(ref.canonicalId, reader) : ingredientName,
    found: false,
    substitutes: [],
    canonicalId: ref.canonicalId,
  };
}

/**
 * Suggestions ordered so the ones the user already owns come first.
 *
 * This is the feature the old code could not have: substitutes were free text
 * with no identity, so "do I own one?" was unanswerable for 192 of the 275
 * simple names. Ordering, not filtering — a substitute you lack is still worth
 * seeing, it just should not be the first thing you read.
 */
export function findSubstitutionsForPantry(
  ingredientName: string,
  ownedIds: ReadonlySet<CanonicalIngredientId>,
  language: string = 'en'
): SubstitutionResult & { ownedCount: number } {
  const result = findSubstitutions(ingredientName, language);
  if (!result.found || ownedIds.size === 0) return { ...result, ownedCount: 0 };

  const owned: Substitute[] = [];
  const rest: Substitute[] = [];
  for (const substitute of result.substitutes) {
    const id = substituteId(substitute.name);
    if (id && ownedIds.has(id)) owned.push(substitute);
    else rest.push(substitute);
  }

  return { ...result, substitutes: [...owned, ...rest], ownedCount: owned.length };
}

/** True when the user owns this substitute right now. */
export function ownsSubstitute(
  substitute: Substitute,
  ownedIds: ReadonlySet<CanonicalIngredientId>
): boolean {
  const id = substituteId(substitute.name);
  return id !== null && ownedIds.has(id);
}

export function getCategories(): string[] {
  const categories = new Set<string>();
  for (const data of Object.values(ingredientSubstitutions)) categories.add(data.category);
  return Array.from(categories).sort();
}

export function getIngredientsByCategory(category: string): string[] {
  const ingredients: string[] = [];
  for (const [name, data] of Object.entries(ingredientSubstitutions)) {
    if (data.category === category) ingredients.push(name);
  }
  return ingredients.sort();
}
